import {
  DeleteOutlined,
  EditFilled,
  FileAddOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useState } from 'react';

import {
  Button,
  Collapse,
  ExemptTag,
  InnerCollapse,
} from '../../../../components/index';
import {
  AllTableItemsType,
  AllTableType,
  ChargeType,
  FareType,
  IndexTablesType,
  TableFiveType,
} from '../../../../typesFinancial';
import {
  createDate,
  formatNumberToLocale,
  maskString,
} from '../../../../utils/utils';
import { ChargeStatusTag } from '../ChargeStatusTag';
import { getFareExemptionTooltip } from '../exemption';
import { MissingRevenueCodeAlert } from '../FareRevenueCode';
import { FareTransmissionDetail } from '../FareTransmissionDetail';
import { FareAllToArchiveModal } from './fareAllToArchiveModal/ModalToArchiveAll';
import { FareAllToBillingModal } from './fareAllToBillingModal/ModalToBillingAll';
import { FareDrawer } from './fareDrawer/FareDrawer';
import { FaresTablesData } from './FaresTablesData';
import { FareStatementDrawer } from './fareStatementDrawer/FareStatementDrawer';
import { FareToArchiveModal } from './fareToArchiveModal/ModalToArchive';
import { ItemTableViewDrawer } from './itemTableViewDrawer/ItemTableViewDrawer';
import { Container } from './styles';

type FaresDataProps = {
  fares: FareType[];
  activeKey: string | string[];
  onOpenPanel: (panel: string | string[]) => void;
  selectedCharge: ChargeType;
  hasEdited: boolean;
  setHasEdited: (value: boolean) => void;
  blockNavigate: boolean;
  setBlockNavigate: (value: boolean) => void;
};

const { Panel } = Collapse;
const { Panel: InnerPanel } = InnerCollapse;

export function FaresData(props: FaresDataProps) {
  const {
    fares,
    activeKey,
    onOpenPanel,
    selectedCharge,
    hasEdited,
    setHasEdited,
    blockNavigate,
    setBlockNavigate,
  } = props;
  const [isNewFareDrawerVisible, setIsNewFareDrawerVisible] = useState(false);
  const [isFareStatementDrawerVisible, setIsFareStatementDrawerVisible] =
    useState(false);
  const [isToArchiveFareDrawerVisible, setIsToArchiveFareDrawerVisible] =
    useState(false);
  const [isToArchiveAllFareDrawerVisible, setIsToArchiveAllFareDrawerVisible] =
    useState(false);
  const [isToBillingAllFareDrawerVisible, setIsToBillingAllFareDrawerVisible] =
    useState(false);

  const [isItemTableViewDrawerVisible, setItemTableViewDrawerVisible] =
    useState(false);
  const [selectedItemTable, setSelectedItemTable] = useState(
    {} as AllTableItemsType
  );
  const [selectedTable, setSelectedTable] = useState(0);
  const [isSelectedTableExempt, setIsSelectedTableExempt] = useState(false);

  const [selectedFare, setSelectedFare] = useState({} as FareType);
  const [isAddNewTable, setIsAddNewTable] = useState(false);

  // necessario criar 2 estados, 1 para saber qual a tabela e outro para retornar os dados da tabela
  const [isEditTable, setIsEditTable] = useState<0 | IndexTablesType>(0);
  const [editTable, setEditTable] = useState({} as AllTableType);

  const [isComplementary, setIsComplementary] = useState(false);

  // Derivado a cada render em vez de guardado em estado: como estado, os flags só
  // subiam para true e nunca voltavam, então desarquivar a fatura não devolvia os
  // botões ao estado correto até a tela remontar.
  const fareStatus = selectedCharge?.fare?.map((fare) => fare.status) || [];
  // `isArchived` é "tem alguma arquivada", e serve para oferecer o desarquivamento.
  // Quem fecha a cobrança para acréscimo é `isFullyArchived`, espelhando o
  // `is_fully_archived` do backend: enquanto houver tarifa viva ainda há trabalho
  // ali, e uma arquivada sozinha não decide o estado da cobrança, que é a decisão 18
  // do change de `tos-core`. Com `isArchived` no lugar dele, arquivar a tarifa que
  // falhou no envio trancava a cobrança inteira, e a saída para cobrar aqueles itens,
  // que é uma tarifa nova aqui mesmo, ficava fora de alcance justamente no caso que
  // precisa dela.
  const isArchived = fareStatus.includes('ARCHIVED');
  const isFullyArchived =
    fareStatus.length > 0 &&
    fareStatus.every((status) => status === 'ARCHIVED');
  // Os três status que o `charge_service` move no arquivamento em massa. Sem o
  // `SEND_ERROR` aqui, a cobrança cuja única tarifa ativa falhou no envio não
  // recebia o botão da ação que arquivaria justamente ela.
  const isBilling = fareStatus.some((status) =>
    ['BILLING', 'REVERSED', 'SEND_ERROR'].includes(status || '')
  );

  function onClickEdit(
    e: React.MouseEvent<HTMLElement>,
    fare: FareType,
    isAddNewTableProp?: boolean,
    indexTable?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9,
    editTableProp = {} as AllTableType
  ) {
    e.stopPropagation();

    if (isAddNewTableProp) setIsAddNewTable(true);
    if (indexTable) setIsEditTable(indexTable);
    if (indexTable && editTableProp && indexTable === 5) {
      setEditTable({
        ...editTableProp,
        items:
          editTableProp?.items?.map((item) => ({
            ...item,
            cargo_arrival_date: item.cargo_arrival_date
              ? createDate(item.cargo_arrival_date)
              : null,
            cargo_departure_date: item.cargo_departure_date
              ? createDate(item.cargo_departure_date)
              : null,
          })) || [],
      });
    } else if (editTableProp) setEditTable(editTableProp);

    const formattedFare: FareType = {
      ...fare,
      table_five: {
        ...(fare.table_five || ({} as TableFiveType)),
        items:
          fare.table_five?.items.map((item) => ({
            ...item,
            cargo_arrival_date: item.cargo_arrival_date
              ? createDate(item.cargo_arrival_date)
              : null,
            cargo_departure_date: item.cargo_departure_date
              ? createDate(item.cargo_departure_date)
              : null,
          })) || [],
      },
    };
    setSelectedFare(formattedFare);
    setIsNewFareDrawerVisible(true);
  }

  function getHeader(fare: FareType) {
    return (
      <div className="header-fare-company">
        <div className="header-fare-company-identity">
          <span className="strong">{fare.company.name}</span>
          <span className="thin">
            {fare.company.cnpj
              ? maskString(fare.company.cnpj, '##.###.###/####-##')
              : fare.company.tin}
          </span>
          <ChargeStatusTag
            style={{ fontSize: '12px' }}
            status={fare.status || 'BILLING'}
          />
          {fare.is_exempt && (
            <ExemptTag tooltip={getFareExemptionTooltip(fare.status)} />
          )}
          <FareTransmissionDetail fare={fare} />
        </div>
        <MissingRevenueCodeAlert fare={fare} />
      </div>
    );
  }

  function extraHeaderTitle(fare: FareType) {
    return (
      <div className="extra-header-fare-company">
        <span className="fare-total">
          {formatNumberToLocale(fare.total_value || 0, true)}
        </span>
        <Button
          className="button-edit-fare"
          icon={<EditFilled />}
          htmlType="button"
          type="text"
          onClick={(e) => onClickEdit(e, fare)}
          disabled={
            fare.status === 'BILLED' ||
            fare.status === 'REBILLED' ||
            fare.status === 'PROCESSING'
          }
        >
          Editar
        </Button>
      </div>
    );
  }

  function handleCloseItem() {
    setItemTableViewDrawerVisible(false);
    setSelectedItemTable({} as AllTableItemsType);
    setSelectedTable(0);
    setIsSelectedTableExempt(false);
  }

  // Atende tanto "Gerar demonstrativo" quanto "Ver demonstrativo": só abre o
  // drawer. O aviso de arquivamento parcial fica no clique que fatura, dentro do
  // próprio drawer.
  function openStatementDrawer(fare: FareType) {
    setSelectedFare(fare);
    setIsFareStatementDrawerVisible(true);
  }

  function openATorchiveFireDrawer(fare: FareType) {
    setSelectedFare(fare);
    setIsToArchiveFareDrawerVisible(true);
  }

  function onCloseDrawer() {
    setSelectedFare({} as FareType);
    setIsAddNewTable(false);
    setIsEditTable(0);
    setEditTable({} as AllTableType);
    setHasEdited(false);
    setBlockNavigate(false);
    setIsComplementary(false);
  }

  return (
    <>
      <ItemTableViewDrawer
        isVisible={isItemTableViewDrawerVisible}
        selectedItem={selectedItemTable}
        selectedTable={selectedTable}
        selectedCharge={selectedCharge}
        isExempt={isSelectedTableExempt}
        onClose={handleCloseItem}
      />
      <FareStatementDrawer
        isVisible={isFareStatementDrawerVisible}
        setIsVisible={setIsFareStatementDrawerVisible}
        selectedFare={selectedFare}
        selectedCharge={selectedCharge}
      />
      <FareToArchiveModal
        showModal={isToArchiveFareDrawerVisible}
        setShowModal={setIsToArchiveFareDrawerVisible}
        selectedFare={selectedFare}
        selectedCharge={selectedCharge}
      />
      <FareAllToArchiveModal
        showModal={isToArchiveAllFareDrawerVisible}
        setShowModal={setIsToArchiveAllFareDrawerVisible}
        selectedCharge={selectedCharge}
      />
      <FareAllToBillingModal
        showModal={isToBillingAllFareDrawerVisible}
        setShowModal={setIsToBillingAllFareDrawerVisible}
        selectedCharge={selectedCharge}
      />
      <FareDrawer
        isVisible={isNewFareDrawerVisible}
        setIsVisible={setIsNewFareDrawerVisible}
        selectedCharge={selectedCharge}
        hasEdited={hasEdited}
        setHasEdited={setHasEdited}
        blockNavigate={blockNavigate}
        setBlockNavigate={setBlockNavigate}
        selectedFare={selectedFare}
        isAddNewTable={isAddNewTable}
        isEditTable={isEditTable}
        editTable={editTable}
        onCloseDrawer={onCloseDrawer}
        isComplementary={isComplementary}
        setSelectedFare={setSelectedFare}
      />
      <Container>
        <Collapse
          activeKey={activeKey}
          key="fares"
          onChange={onOpenPanel}
          expandIconPosition="end"
        >
          <Panel forceRender header="Tarifas" key="fares">
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                icon={<PlusOutlined />}
                type="primary"
                style={{ marginBottom: '16px' }}
                onClick={() => setIsNewFareDrawerVisible(true)}
                disabled={isFullyArchived}
              >
                Novo responsável
              </Button>
              <Button
                icon={<FileAddOutlined />}
                style={{ marginBottom: '16px' }}
                onClick={() => {
                  setIsNewFareDrawerVisible(true);
                  setIsComplementary(true);
                }}
                disabled={isFullyArchived}
              >
                Complementar fatura - {selectedCharge.status}
              </Button>
              {isBilling && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  style={{ marginBottom: '16px' }}
                  onClick={() => {
                    setIsToArchiveAllFareDrawerVisible(true);
                  }}
                >
                  Arquivar todos
                </Button>
              )}
              {isArchived && (
                <Button
                  type="text"
                  style={{ marginBottom: '16px' }}
                  onClick={() => {
                    setIsToBillingAllFareDrawerVisible(true);
                  }}
                >
                  Desarquivar todos
                </Button>
              )}
            </div>
            {fares.map((fare) => (
              <InnerCollapse
                key={fare.id}
                expandIconPosition="end"
                defaultActiveKey={fares.map((fare) => `fare_${fare.id}`)}
              >
                <InnerPanel
                  key={`fare_${fare.id}`}
                  header={getHeader(fare)}
                  extra={extraHeaderTitle(fare)}
                >
                  {fare.complementaries && fare.complementaries.length > 0 && (
                    <>
                      <hr className="division-line" />
                      <FaresTablesData
                        fare={fare.complementaries[0]}
                        setSelectedItemTable={setSelectedItemTable}
                        setItemTableViewDrawerVisible={
                          setItemTableViewDrawerVisible
                        }
                        onClickEdit={onClickEdit}
                        setSelectedTable={setSelectedTable}
                        setIsSelectedTableExempt={setIsSelectedTableExempt}
                        title="FATURA COMPLEMENTAR"
                        showStatus
                        chargeId={selectedCharge.id}
                        openStatementDrawer={openStatementDrawer}
                        openATorchiveFireDrawer={openATorchiveFireDrawer}
                      />
                    </>
                  )}
                  <hr className="division-line" />
                  <FaresTablesData
                    fare={fare}
                    setSelectedItemTable={setSelectedItemTable}
                    setItemTableViewDrawerVisible={
                      setItemTableViewDrawerVisible
                    }
                    onClickEdit={onClickEdit}
                    setSelectedTable={setSelectedTable}
                    setIsSelectedTableExempt={setIsSelectedTableExempt}
                    openStatementDrawer={openStatementDrawer}
                    openATorchiveFireDrawer={openATorchiveFireDrawer}
                    chargeId={selectedCharge.id}
                  />
                </InnerPanel>
              </InnerCollapse>
            ))}
          </Panel>
        </Collapse>
      </Container>
    </>
  );
}
